import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import ContactButton from '../components/ContactButton';

const AllCollections = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const response = await api.get('/shopify/collections');
      setCollections(response.data.collections || []);
    } catch (error) {
      console.error('Failed to load collections:', error);
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const getCollectionImage = (collection) => {
    // Use Shopify image if available, otherwise fall back to default images
    if (collection.image && collection.image.url) {
      return collection.image.url;
    }
    
    // Default images for specific collections (fallback)
    const collectionImages = {
      'archive': 'https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/ygs0x6wd_IMG_2794.JPG',
      'footwear': 'https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/vyjmxr5z_IMG_2792.JPG',
      'outerwear': 'https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/aeoxnjzq_IMG_2796.JPG',
      'prsvr-leather': 'https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/aeoxnjzq_IMG_2796.JPG',
      'the-taylor-suit': 'https://customer-assets.emergentagent.com/job_prsvr-uniforms/artifacts/i3qamr5f_IMG_3259.jpeg',
      'prsvr-runway': 'https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/zjvlrtug_IMG_2798.PNG'
    };
    
    return collectionImages[collection.handle] || 'https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG';
  };

  return (
    <div className="min-h-screen bg-cream overflow-x-hidden">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-charcoal/95 backdrop-blur-md border-b border-warmSage/20">
        <nav className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_fashion-empire-2/artifacts/bcsdxd12_IMG_2797.PNG" 
                alt="PRSVR Logo" 
                className="h-10"
                style={{ filter: 'brightness(0) saturate(100%) invert(98%) sepia(6%) saturate(214%) hue-rotate(327deg) brightness(102%) contrast(96%)' }}
              />
            </a>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-cream hover:text-warmSage text-sm sm:text-base">
                Home
              </Button>
              <Button onClick={() => navigate('/shop')} className="bg-warmSage text-charcoal hover:bg-warmSage/90 text-sm sm:text-base">
                Shop All
              </Button>
            </div>
          </div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 pt-24 sm:pt-32 pb-16 max-w-full">
        {/* Page Header */}
        <div className="text-center mb-16">
          <Badge className="bg-charcoal text-cream mb-4">PRSVR COLLECTIONS</Badge>
          <h1 className="text-6xl font-black text-charcoal uppercase mb-6">
            All Collections
          </h1>
          <p className="text-xl text-warmGray max-w-3xl mx-auto leading-relaxed">
            Explore our curated collections. Each one tells a story of perseverance, style, and elevated lifestyle.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-charcoal border-t-warmSage"></div>
            <p className="mt-4 text-warmGray">Loading collections...</p>
          </div>
        )}

        {/* Collections Grid */}
        {!loading && collections.length > 0 && (
          <div className="grid md:grid-cols-3 gap-8">
            {collections.map((collection) => (
              <Card
                key={collection.id}
                className="product-card-streetwear group cursor-pointer overflow-hidden"
                onClick={() => navigate(`/collections/${collection.handle}`)}
              >
                <div className="relative overflow-hidden aspect-[4/3] bg-warmGray/10">
                  <img
                    src={getCollectionImage(collection)}
                    alt={collection.title}
                    className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="product-overlay-streetwear">
                    <Button className="btn-primary-streetwear btn-lg">
                      BROWSE COLLECTION
                    </Button>
                  </div>
                </div>
                <CardContent className="p-8 bg-charcoal">
                  <h3 className="product-title-streetwear text-2xl mb-3 uppercase">
                    {collection.title}
                  </h3>
                  <p className="product-desc-streetwear mb-4 line-clamp-2">
                    {collection.description || 'Discover our latest pieces'}
                  </p>
                  <Button 
                    size="sm" 
                    className="btn-primary-streetwear group-hover:bg-warmSage group-hover:text-charcoal"
                  >
                    EXPLORE <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* No Collections */}
        {!loading && collections.length === 0 && (
          <div className="text-center py-20">
            <h3 className="text-2xl font-bold text-charcoal mb-2">No collections available</h3>
            <p className="text-warmGray mb-6">Check back soon for new collections</p>
            <Button onClick={() => navigate('/shop')}>
              Browse All Products
            </Button>
          </div>
        )}
      </div>

      <ContactButton />
    </div>
  );
};

export default AllCollections;
